import React, { ReactNode, memo, useEffect, useMemo } from "react";
import cn from "classnames";
import styles from "./MinerRow.module.scss";
import { useIntl } from "react-intl";
import { draggableHTMLID } from "@store/minersSelect/reducer";
import CopyItem from "../../../global/new/CopyItem/CopyItem";
import { onSocketEvent } from "./onSocketEvent";
import { ICustomEventDetail } from "@components/Context/SocketContext";
import { IUnknownMiner } from "@utils/hooks/useGetMinerList";
import useMediaQuery from "@utils/hooks/utils";
import { TableHeadItem } from "../MinersTable/tableHead";
import { colStyle } from "../MinersTable/MinersTable";
import { Element } from "react-scroll";
import psstyles from "./MinerPhoneSizeRow.module.scss";

interface UnknownMinerRowProps {
  miner: IUnknownMiner;
}

/**
  created: Moment;
  login: string;
  agent: string;
  ip: string;
  mac: string;
*/
const unknownMinerTableHeaders: TableHeadItem[] = [
  {
    messageId: "machines.table.unknown.created",
    width: 120,
    height: 100,
  },
  {
    messageId: "machines.table.unknown.login",
    width: 100,
    height: 300,
  },
  {
    messageId: "machines.table.unknown.agent",
    width: 150,
    height: 150,
  },
  {
    messageId: "machines.table.unknown.ip",
    width: 100,
    height: 100,
  },
  {
    messageId: "machines.table.unknown.mac",
    width: 100,
    height: 100,
  },
];

const rowWidth = (head: TableHeadItem[], isAdmin?: boolean) =>
  head.map((item) => item.width).reduce((a, x) => a + x, 0);

const rowHeight = (head: TableHeadItem[], isAdmin?: boolean) =>
  head.map((item) => item.height).reduce((a, x) => a + x, 0);

const rwd = rowWidth(unknownMinerTableHeaders, true);
const rhg = rowHeight(unknownMinerTableHeaders, true);
const wdStyle = (
  column: TableHeadItem,
  isTablet: boolean
): React.CSSProperties => colStyle(column, isTablet, rwd, rhg);
const rowStyles = (isTablet: boolean) =>
  unknownMinerTableHeaders.map((h) => wdStyle(h, isTablet));

function UnknownMinerRow(props: UnknownMinerRowProps) {
  const { formatMessage } = useIntl();
  const { miner } = props;
  const isTablet = useMediaQuery("max", 1025);
  const rstyles = useMemo(() => rowStyles(isTablet), [isTablet]);

  useEffect(() => {
    // dev.log("socket event");
    const eventType = `miner-${miner.id}`;
    document.addEventListener(eventType, onMinerEvent);
    function onMinerEvent(e: Event) {
      const data = (e as CustomEvent).detail as ICustomEventDetail;
      onSocketEvent(data.type, data.event, () => {}); // TODO
    }
    return () => {
      document.removeEventListener(eventType, onMinerEvent);
    };
  }, []);

  return (
    <div className={cn(styles.row_wrapper)} id={draggableHTMLID(`${miner.id}`)}>
      <div
        className={cn(styles.row, styles.after_statuses, styles.isAdmin)}
        data-error-hint={formatMessage({ id: "machines.miner_row.error_hint" })}
      >
        {/**
         *  created: Moment;
            login: string;
            agent: string;
            ip: string;
            mac: string;
         */}
        <CopyItem
          className={styles.table_item}
          style={{ ...rstyles[0], textAlign: "left" }}
          copyClassName={styles.opacityClass}
          text={miner.created.toLocaleString()}
        >
          {miner.created.toLocaleString()}
        </CopyItem>
        <CopyItem
          className={styles.table_item}
          style={{ ...rstyles[1], textAlign: "left" }}
          copyClassName={styles.opacityClass}
          text={miner.login}
        >
          {miner.login}
        </CopyItem>
        <CopyItem
          className={styles.table_item}
          style={{ ...rstyles[2], textAlign: "left" }}
          copyClassName={styles.opacityClass}
          text={miner.agent}
        >
          {miner.agent}
        </CopyItem>
        <CopyItem
          className={styles.table_item}
          style={{ ...rstyles[3], textAlign: "left" }}
          copyClassName={styles.opacityClass}
          text={miner.ip}
        >
          {miner.ip}
        </CopyItem>
        <CopyItem
          className={styles.table_item}
          style={{ ...rstyles[4], textAlign: "left" }}
          copyClassName={styles.opacityClass}
          text={miner.mac}
        >
          {miner.mac}
        </CopyItem>
      </div>
    </div>
  );
}

interface UnknownTableHeaderProps {}

export function UnknownTableHeader(props: UnknownTableHeaderProps) {
  const { formatMessage } = useIntl();
  const isDesktop = useMediaQuery("min", 1025); // see variables.scss / $viewport--md
  const isTablet = useMediaQuery("max", 1025);
  const rstyles = useMemo(() => rowStyles(isTablet), [isTablet]);

  return (
    <>
      {unknownMinerTableHeaders.map((item, i) => (
        <div
          className={styles.head_item}
          key={i}
          style={{
            ...rstyles[i],
            ...(isDesktop && i === unknownMinerTableHeaders.length - 1
              ? { justifyContent: "flex-end" }
              : {}),
          }}
        >
          <label>{formatMessage({ id: item.messageId })}</label>
        </div>
      ))}
    </>
  );
}

export interface UnknownMinerPhoneSizeRowProps extends UnknownMinerRowProps {
  pos: number;
}

interface RowItemPropsI {
  headItem: TableHeadItem;
  children: ReactNode;
  isFirst?: boolean;
  isLast?: boolean;
  isOdd?: boolean;
}

function RowItem(props: RowItemPropsI) {
  const { formatMessage } = useIntl();

  return (
    <div className={psstyles.rowItem}>
      <div className={psstyles.head}>
        {formatMessage({ id: props.headItem.messageId })}
      </div>
      <div
        className={cn(psstyles.data, {
          [psstyles.first]: props.isFirst,
          [psstyles.last]: props.isLast,
          [psstyles.odd]: props.isOdd,
        })}
      >
        {props.children}
      </div>
    </div>
  );
}

export function UnknownMinerPhoneSizeRow(props: UnknownMinerPhoneSizeRowProps) {
  const { miner } = props;

  return (
    <Element className={cn(psstyles.container)} name={miner.id.toString()}>
      <div
        className={cn(psstyles.row, {
          [styles.odd]: props.pos % 2,
        })}
      >
        <RowItem headItem={unknownMinerTableHeaders[0]} isFirst isOdd>
          {miner.created.toLocaleString()}
        </RowItem>
        <RowItem headItem={unknownMinerTableHeaders[1]}>{miner.login}</RowItem>
        <RowItem headItem={unknownMinerTableHeaders[2]} isOdd>
          {miner.agent}
        </RowItem>
        <RowItem headItem={unknownMinerTableHeaders[3]}>{miner.ip}</RowItem>
        <RowItem headItem={unknownMinerTableHeaders[4]} isOdd isLast>
          {miner.mac}
        </RowItem>
      </div>
    </Element>
  );
}

export default memo(
  UnknownMinerRow,
  (prev: UnknownMinerRowProps, curr: UnknownMinerRowProps) => {
    return JSON.stringify(prev) === JSON.stringify(curr);
  }
);
